import {
  type ActivityDomainSelectModel,
  type NotificationUrlSlug,
  ProjectId,
} from '@mntn-dev/domain-types'
import { UnhandledUnionError } from '@mntn-dev/utilities'

import {
  dashboardRoute,
  projectActivityRoute,
  projectFileRoute,
  projectPostProductionReviewRoute,
  projectPreProductionReviewRoute,
  projectRoute,
  projectServiceRoute,
  projectSurveyRoute,
} from '../facades.ts'
import type { Route, RouteKey } from '../route.ts'

export const activityLinkRoute = (
  activity: ActivityDomainSelectModel,
  slug?: NotificationUrlSlug
): Route<RouteKey> => {
  const { details } = activity

  switch (details.activityType) {
    case 'comment_created': {
      const { feedId, activityId } = activity
      return projectActivityRoute({
        projectId: ProjectId(feedId),
        activityId,
      })
    }

    case 'project_bid_rejected': {
      return dashboardRoute()
    }

    case 'post_production_review_brand_feedback_submitted':
    case 'post_production_review_maker_proposal_submitted': {
      const { projectId } = details.data
      return projectPostProductionReviewRoute({ projectId })
    }

    case 'project_status_changed': {
      const { projectId, status } = details.data

      if (status === 'post_production_complete') {
        return projectPostProductionReviewRoute({ projectId })
      }

      if (status === 'complete' && slug === 'survey') {
        return projectSurveyRoute({
          projectId,
          surveyType: 'project-completed-brand-reviews-maker',
        })
      }

      return projectRoute({ projectId })
    }

    case 'advance_payment_status_changed':
    case 'agreement_terms_accepted':
    case 'project_bid_accepted':
    case 'project_bidding_closed':
    case 'project_bidding_opened':
    case 'project_bidding_successful':
    case 'project_bidding_unsuccessful':
    case 'project_closed':
    case 'project_created':
    case 'project_offer_expired':
    case 'project_offer_pending':
    case 'project_offers_sent': {
      const { projectId } = details.data
      return projectRoute({ projectId })
    }

    case 'project_file_added': {
      const { projectId, fileId } = details.data
      return projectFileRoute({ projectId, fileId })
    }

    case 'pre_production_review_brand_feedback_submitted':
    case 'pre_production_review_maker_proposal_submitted': {
      const { projectId, projectServiceId } = details.data
      return projectPreProductionReviewRoute({ projectId, projectServiceId })
    }

    case 'project_service_added': {
      const { projectId, projectServiceId } = details.data
      return projectServiceRoute({ projectId, projectServiceId })
    }

    default:
      throw new UnhandledUnionError(details)
  }
}
